import { useEffect, useState } from "react";
import { getTradingIdeasAccess } from "@/controllers/trading-ideas";

// Whether the current user can see the Trading Ideas feature (staff +
// a backend-managed group — the group membership is only knowable
// server-side, so this can't come from userState entitlements the way
// requiresOptionsPlatform does). Fetched once per page load and shared
// across consumers (menu + page) via module memory, mirroring
// useNoBackgroundLogos — but deliberately NOT persisted to
// localStorage: it's permission-sensitive and cheap to re-check per
// session.
//
// null = still checking; boolean = resolved. A failed check resolves
// to false (fail closed) but clears the in-flight promise so a later
// mount can retry after a transient network error.
let accessState: boolean | null = null;
let fetchPromise: Promise<boolean> | null = null;

// Clears the cached answer so the next mount re-probes. Called by the
// Trading Ideas page when the list endpoint 403s — access revoked
// mid-session, or a different user after an SPA re-login. Known
// limitation: there is no repo-wide logout event that module-memory
// caches subscribe to (useNoBackgroundLogos doesn't reset either),
// so a stale answer can survive a same-tab user swap until the next
// 403 — in practice the main logout path (UserMenu) hard-navigates
// to /login, which resets module memory anyway.
export const resetTradingIdeasAccess = () => {
    accessState = null;
    fetchPromise = null;
};

const ensureChecked = (): Promise<boolean> => {
    if (accessState !== null) return Promise.resolve(accessState);
    if (!fetchPromise) {
        fetchPromise = getTradingIdeasAccess()
            .then((r) => {
                accessState = !!r.enabled;
                return accessState;
            })
            .catch(() => {
                fetchPromise = null;
                return false;
            });
    }
    return fetchPromise;
};

export const useTradingIdeasAccess = (): { access: boolean | null } => {
    const [access, setAccess] = useState<boolean | null>(accessState);

    useEffect(() => {
        if (accessState !== null) {
            setAccess(accessState);
            return;
        }
        let mounted = true;
        ensureChecked().then((enabled) => mounted && setAccess(enabled));
        return () => {
            mounted = false;
        };
    }, []);

    return { access };
};
